import { generatePath } from 'react-router';

import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import * as allTypes from 'constants/ActionTypes';
import { SELECT_DEALER } from 'constants/ActionTypes';
import { CONVERSATION_TYPES, DASHBOARD_SOURCE, STATUS, UI_TABS } from 'constants/ChatConstants';
import { PaginationType } from 'constants/Global';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import * as actions from 'modules/chat/actions';
import { fetchConversationAttachments, unmountConversationAttachmentsPane } from 'modules/chat/actions';
import * as dashboardAction from 'modules/chat/actions/dashboard';
import * as detailsAction from 'modules/chat/actions/details';
import * as actionTypes from 'modules/chat/actionTypes';
import { CONVERSATION_CONTEXT_SELECT, CONVERSATION_HISTORY_MEDIA } from 'modules/chat/constant';

import { getContextById } from 'modules/chat/selectors/context';
import { getLatestContext } from 'modules/chat/selectors/context.select';
import {
  getActiveTab,
  getConversationById,
  getSelectedConversation,
  getSelectedConversationId,
  canShowClientConversation,
  isCustomerConversationsLoading,
  isInternalConversationsLoading,
} from 'modules/chat/selectors/dashboard';
import { getConversationMessagesRequestParams, getMediaDrawerContextId } from 'modules/chat/selectors/details';
import { paramsFormatter } from 'modules/chat/utils';

import { selectDealer } from 'modules/dealers/actions';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';

import { getCurrentUserId, isImpersonatingUser } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { getRequestParams } from '../selectors/request';

const FILTER_TYPES = [
  actionTypes.SET_CHAT_FILTER,
  actionTypes.SET_CHAT_FILTERS,
  actionTypes.CLEAR_CHAT_FILTER,
  actionTypes.REMOVE_CHAT_FILTER,
];

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [actionTypes.FETCH_CONVERSATION_ATTACHMENTS]: {
    callbacks: {
      success: (payload, meta) => actions.fetchConversationAttachmentsSuccess(payload, meta),
    },
  },
};

/** Refresh the conversations tab */
function* refreshConversationsTab() {
  const activeTab = yield select(getActiveTab);
  yield put(
    dashboardAction.fetchConversations({
      types: activeTab === UI_TABS.INTERNAL ? CONVERSATION_TYPES.INTERNAL : CONVERSATION_TYPES.EXTERNAL,
    })
  );
}

/** Clear all conversations */
function* clearConversations() {
  yield all([put(dashboardAction.clearCustomerConversations()), put(dashboardAction.clearInternalConversations())]);
}

/** Get the tab that should be active based on the selected conversation */
function* getTabByConversation() {
  const conversationId = yield select(getSelectedConversationId);
  const currentUserId = yield select(getCurrentUserId);
  const { participantIds = [], type } = yield select(state => getConversationById(state, conversationId));

  if (type === CONVERSATION_TYPES.EXTERNAL) {
    const isParticipants = participantIds.includes(currentUserId);
    return isParticipants ? UI_TABS.MY_CLIENT : UI_TABS.ALL_CLIENT;
  }
  return UI_TABS.INTERNAL;
}

/** Fetch conversation details and messages */
function* fetchConversationSideEffect() {
  const conversation = yield select(getSelectedConversation);
  const { id: conversationId, type: conversationType } = conversation;
  const isClientConversation = conversationType === CONVERSATION_TYPES.EXTERNAL;

  // Fetch conversation's messages
  yield put(
    detailsAction.fetchMessages({
      conversationId,
      paginationType: PaginationType.SLICE,
    })
  );

  // If it's a client conversation, fetch its contexts
  if (isClientConversation) {
    const { payload, error } = yield putAndWait(
      detailsAction.fetchConversationContext(conversationId, {}, { source: CONVERSATION_CONTEXT_SELECT })
    );
    if (!error) {
      // Select the first context if available
      const selectedId = payload.content[0]?.id;
      if (selectedId) {
        yield put(detailsAction.setContextId(selectedId));
      }
    }
  }
}

/**
 * Redirect to the correct conversation when a new one is created
 * @param {import('../types').CreateInterventionSuccessAction} action
 */
function* createConversationSideEffect({ payload: conversation }) {
  const { id: conversationId } = conversation;
  yield call(navigate, generatePath(PATH.CHAT.path, { conversationId }), { replace: true });
}

/**
 * Force to refresh the conversation with new messages
 * @param {import('../types').FetchUnreadCountsByTypeSuccessAction} action
 */
function* afterGetCountByType({ payload }) {
  const toPut = [];
  if (payload.internal > 0) {
    const isLoading = yield select(isInternalConversationsLoading);
    if (!isLoading) {
      toPut.push(put(dashboardAction.fetchConversations({ types: CONVERSATION_TYPES.INTERNAL })));
    }
  }

  if (payload.external.all > 0) {
    const isLoading = yield select(isCustomerConversationsLoading);
    if (!isLoading) {
      toPut.push(put(dashboardAction.fetchConversations({ types: CONVERSATION_TYPES.EXTERNAL })));
    }
  }

  if (toPut.length > 0) {
    yield all(toPut);
  }
}

/**
 * Side effect to handle after fetching conversations
 *   - Mark the current conversation as read if it has unread messages
 * @param {import('../types').FetchCustomerConversationsFailureAction|import('../types').FetchCustomerConversationsSuccessAction} action
 */
function* fetchConversationsSideEffect({ payload, meta }) {
  const conversationId = yield select(getSelectedConversationId);
  const isCustomerAction = meta.type === CONVERSATION_TYPES.EXTERNAL;

  if (isCustomerAction && conversationId) {
    const conversation = payload.content.find(({ id }) => id === conversationId);
    const haveUnreadMessage = conversation?.unreadCount > 0;

    if (haveUnreadMessage) {
      const isImpersonating = yield select(isImpersonatingUser);
      const { error } = yield putAndWait(
        detailsAction.fetchMessages({ conversationId, paginationType: PaginationType.SLICE })
      );

      if (!isImpersonating && !error) {
        yield put(dashboardAction.patchConversationStatus(conversationId, STATUS.READ));
      }
    }
  }
}

function* refreshAllFromDashboard() {
  yield all([call(clearConversations), call(refreshConversationsTab)]);
}

/** @param {import('../types').SetHistoryIdAction} action */
function* setHistoryIdSideEffect({ payload }) {
  const params = yield select(getConversationMessagesRequestParams);
  yield put(detailsAction.fetchMessages(params));

  /** Change the selected context, If the historyId is not present, fallback to the latest context */
  const latestContext = yield select(getLatestContext);
  const newContextId = payload.historyId ?? latestContext.id;
  yield put(detailsAction.setContextId(newContextId));
}

function* updateDealerAndTabs() {
  const selectedDealerId = yield select(getSelectedDealerId);
  const conversationId = yield select(getSelectedConversationId);
  const conversation = yield select(state => getConversationById(state, conversationId));
  const { dealerId } = conversation;

  // First, check if the conversation's dealer is the same as the selected dealer
  if (dealerId !== selectedDealerId) {
    yield all([call(clearConversations), put(selectDealer(dealerId))]);
    const { error } = yield take([allTypes.FETCH_DEALER_SUCCESS, allTypes.FETCH_DEALER_FAILURE]);
    if (error) {
      throw new Error(`Failed to select dealer ${dealerId}`);
    }
  }

  // Update the active tab based on the conversation type
  const currentActiveTab = yield select(getActiveTab);
  const shouldBeSelectedTab = yield call(getTabByConversation);
  if (shouldBeSelectedTab !== currentActiveTab) {
    yield put(dashboardAction.setActiveTabs(shouldBeSelectedTab));
  }
}

function* initializeChat() {
  const conversationId = yield select(getSelectedConversationId);
  yield put(dashboardAction.setInitialize(false));

  // If there's a conversationId in the URL, select it and fetch its details
  if (conversationId) {
    const { error } = yield putAndWait(detailsAction.fetchConversation(conversationId, { source: DASHBOARD_SOURCE }));
    if (!error) {
      // Ensure dealer and tab are correctly set
      yield call(updateDealerAndTabs);
      // Fetch conversation details and messages
      yield call(fetchConversationSideEffect);
    }
  }

  // Fetch conversations for the default active tab
  yield call(refreshConversationsTab);
  yield put(dashboardAction.isReady());
}

/** Open media drawer side effect - fetch attachments */
function* openMediaDrawerSideEffect() {
  const contextId = yield select(getMediaDrawerContextId);
  const conversationId = yield select(getSelectedConversationId);
  const context = yield select(state => getContextById(state, contextId));

  const params = paramsFormatter(context, conversationId);
  yield put(fetchConversationAttachments(params, { source: CONVERSATION_HISTORY_MEDIA }));
}

/** Close media drawer side effect - cleanup */
function* closeMediaDrawerSideEffect() {
  yield put(unmountConversationAttachmentsPane());
}

/**
 * Check if the conversation is covered by the current filters and should be displayed
 * @param {string} conversationId
 * @returns {boolean}
 */
function* shouldDisplayConversation(conversationId) {
  const { messageStatus: _messageStatus, ...baseParams } = yield select(getRequestParams);

  const showExternal = yield select(canShowClientConversation);
  const type = showExternal ? CONVERSATION_TYPES.EXTERNAL : CONVERSATION_TYPES.INTERNAL;

  const params = { ...baseParams, page: 0, size: 1, types: type, ids: [conversationId] };
  const { payload, error } = yield putAndWait(dashboardAction.genericFetchConversations(params));

  return !error && Boolean(payload?.content?.at(0));
}

/**
 * Return the conversation, fetching it if not present
 * @param {string} conversationId
 * @returns {?import("../types").Conversation}
 */
function* getConversation(conversationId) {
  const conversation = yield select(state => getConversationById(state, conversationId));
  if (conversation?.type) {
    return conversation;
  }

  const { payload, error } = yield putAndWait(detailsAction.fetchConversation(conversationId));
  return !error ? payload : null;
}

/**
 * After receiving a new message from socket, prepend the conversation if needed
 * @param {import("../types").UpdateMessageFromSocketAction} action
 */
function* afterFetchMessageFromSocket({ payload: { message, isNew } }) {
  if (isNew) {
    const { conversationId } = message;

    const shouldPrepend = yield call(shouldDisplayConversation, conversationId);
    if (shouldPrepend) {
      const conversation = yield call(getConversation, conversationId);
      if (conversation) {
        yield put(dashboardAction.prependConversationInList(conversation));
      }
    }
  }
}

const fetchConversationSuccessChannel = ({ type, meta }) =>
  type === actionTypes.FETCH_CONVERSATION_SUCCESS && meta.source === DASHBOARD_SOURCE;

function* initializeChatWorker() {
  yield call(initializeChat);

  // Actions scoped to the chat page
  yield takeEvery(LOCATION_CHANGE, fetchConversationSideEffect);
  yield takeLatest(actionTypes.SET_HISTORY_ID, setHistoryIdSideEffect);
  yield takeLatest(fetchConversationSuccessChannel, fetchConversationSideEffect);
  yield takeLatest(actionTypes.POST_CONVERSATION_SUCCESS, createConversationSideEffect);
  yield takeLatest(actionTypes.FETCH_UNREAD_COUNTS_BY_TYPE_SUCCESS, afterGetCountByType);
  yield takeLatest(actionTypes.FETCH_CONVERSATIONS_SUCCESS, fetchConversationsSideEffect);
  yield takeLatest([...FILTER_TYPES, actionTypes.SET_ACTIVE_TABS, SELECT_DEALER], refreshAllFromDashboard);
  yield takeLatest(actionTypes.OPEN_MEDIA_DRAWER, openMediaDrawerSideEffect);
  yield takeLatest(actionTypes.CLOSE_MEDIA_DRAWER, closeMediaDrawerSideEffect);

  yield takeEvery(actionTypes.RECEIVE_MESSAGE_FROM_SOCKET, afterFetchMessageFromSocket);
}

const channelPattern = ({ type, payload }) => type === LOCATION_CHANGE && payload.action !== 'REPLACE';

export default function* chatDetailsSagas() {
  // Request
  yield all(simpleRequestsLoader(simpleRequests));

  // On Chat page
  yield scopeToPage([PATH.CHAT], initializeChatWorker, undefined, channelPattern);
}
